import React from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Ticket, Calendar, ArrowRight, Download } from "lucide-react";
import { money } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Confirmation() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const b = state?.booking;

  if (!b) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <p className="text-slate-500">No booking to show.</p>
      <Button className="mt-4 rounded-full bg-brand" onClick={() => navigate("/")}>Home</Button>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200 }} className="text-center">
        <div className="w-20 h-20 rounded-full bg-emerald-50 flex items-center justify-center mx-auto"><CheckCircle2 className="w-11 h-11 text-emerald-500" /></div>
        <h1 className="font-heading font-black text-3xl text-slate-950 mt-6">Booking confirmed!</h1>
        <p className="text-slate-500 mt-2">A confirmation has been sent to {b.passenger?.email}. Bon voyage ✈</p>
      </motion.div>

      <div className="mt-8 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="confirmation-card">
        <div className="bg-slate-950 text-white p-6 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400">Booking reference</div>
            <div className="font-heading font-black text-2xl tracking-wider" data-testid="booking-reference">{b.reference}</div>
          </div>
          <Ticket className="w-10 h-10 text-brand" />
        </div>
        <div className="p-6 space-y-4">
          <h2 className="font-heading font-bold text-lg text-slate-950">{b.title}</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><div className="text-slate-400">Type</div><div className="font-semibold capitalize text-slate-900">{b.type}</div></div>
            <div><div className="text-slate-400">Status</div><div className="font-semibold text-emerald-600 capitalize">{b.status}</div></div>
            <div><div className="text-slate-400">Traveller</div><div className="font-semibold text-slate-900">{b.passenger?.name}</div></div>
            {b.travel_date && <div><div className="text-slate-400">Travel date</div><div className="font-semibold text-slate-900 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{b.travel_date}</div></div>}
          </div>
          <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
            <span className="text-slate-500">Total paid</span>
            <span className="font-heading font-black text-2xl text-slate-950">{money(b.amount, b.currency)}</span>
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <Button onClick={() => navigate("/my-bookings")} data-testid="view-bookings" className="flex-1 rounded-full bg-brand hover:bg-brand-hover h-12 font-heading font-bold gap-2">View my bookings <ArrowRight className="w-4 h-4" /></Button>
        <Button variant="outline" onClick={() => window.print()} className="rounded-full h-12 font-heading font-bold gap-2"><Download className="w-4 h-4" />Save receipt</Button>
      </div>
      <div className="text-center mt-6"><Link to="/" className="text-brand font-bold text-sm">Continue exploring</Link></div>
    </div>
  );
}
